import React from 'react'
import { tokens } from "../../../theme";
import { Box, useTheme, Typography, Checkbox, FormControlLabel, Button } from "@mui/material";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '../../../components/alert/Alert';
import Header from "../../../components/Header";
import AdminActionAccordion from './AdminActionAccordion';
import Axios from 'axios';
import AssignmentBox from './AssignmentBox';
import TestBox from '../tests/TestBox';
import TestBoxV2 from '../tests/TestBoxV2';
import EttExamBox from './EttExamBox';
import EttQbankBox from './EttQbankBox';
import v2Api from '../../../utils/v2Api';
import { useOutletContext } from 'react-router-dom';
import Loading from '../../../components/Loading';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import getCookies from '../../../utils/getCookies';
import { getAuthHeader } from '../../../utils/authToken';
import ReorderHomeworkPageModal from './ReorderHomeworkPageModal';


const Homework = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // for 401 error
    const signOut = useSignOut();
    const navigate = useNavigate();
    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    }

    const matches = useMediaQuery('(min-width:1500px)');
    const matches2 = useMediaQuery('(min-width:1200px)');

    const [loading, setLoading] = React.useState(true);
    const [courseId, setCourseId] = React.useState(null);
    const courseData = useOutletContext().courseData;
    const lectureModules = useOutletContext().lectureModules;
    const [students, setStudents] = React.useState(null);
    const [studentsStartEndDateMap, setStudentsStartEndDateMap] = React.useState(null);
    const [homeworks, setHomeworks] = React.useState(null);
    const [authorDisabledForAdminActions, setAuthorDisabledForAdminActions] = React.useState(false);
    const currentUser = getCookies("_auth_state");
    const userId = currentUser ? currentUser.split(',')[0]?.split(':')[1]?.split('"')[1] : null;
    const userRole = currentUser ? currentUser.split(',')[2]?.split(':')[1]?.split('"')[1] : null;
    const homeworkIdFromUrl = window.location.pathname.split('/')[4] === undefined ? null : window.location.pathname.split('/')[4];
    const homeworkIdFromUrlRef = React.useRef(null);
    const [classMaterials, setClassMaterials] = React.useState([]);
    const [studentId, setStudentId] = React.useState(null);
    const [homeworkTests, setHomeworkTests] = React.useState([]);
    const [v2HomeworkTests, setV2HomeworkTests] = React.useState([]);
    const [prepItems, setPrepItems] = React.useState({ exams: [], qbank: [] });
    const [homeworkPageOrder, setHomeworkPageOrder] = React.useState(null);

    // Homework type settings (admin only)
    const [allowFileUpload, setAllowFileUpload] = React.useState(true);
    const [allowHwQuestions, setAllowHwQuestions] = React.useState(true);
    const [hasUnsavedSettings, setHasUnsavedSettings] = React.useState(false);
    const [savingSettings, setSavingSettings] = React.useState(false);


    // for backdrop
    const [backdropOpen, setBackdropOpen] = React.useState(false);
    const handleBackdropClose = () => {
        setBackdropOpen(false);
    };

    // for alert
    const [successAlertOpen, setSuccessAlertOpen] = React.useState(false);
    const [errorAlertOpen, setErrorAlertOpen] = React.useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = React.useState("");
    const [errorAlertMessage, setErrorAlertMessage] = React.useState("");

    // useEffect
    React.useEffect(() => {
        localStorage.setItem('selectedMenu', 'Homework');
        setCourseId(window.location.pathname.split('/')[2]);

        if (userRole === "student" || userRole === "teacher")
            setAuthorDisabledForAdminActions(true);

        async function getHomework() {
            await Axios.post(`${process.env.REACT_APP_URL}/api/v1/courses/${window.location.pathname.split('/')[2]}/homeworks`, {
                is_student: userRole === "student" ? true : false,
                student_uuid: userRole === "student" ? userId : null
            }, {
                headers: {
                    Authorization: getAuthHeader(),
                }
            }).then((res) => {
                setHomeworks(res.data.all_homework);
            }).catch((err) => {
                if (err.response?.status === 401) {
                    handleLogout();
                    return;
                }
                console.log(err);
            });
        }

        // Phase 7 — prep surface: assigned ETT exams + qbank chapters with deep-links.
        // Students get their personal view (student_uuid); teachers/admins get the
        // per-student roster (no student_uuid → the endpoint returns status per student).
        async function getPrepItems() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${window.location.pathname.split('/')[2]}/prep-items`, {
                headers: { Authorization: getAuthHeader() },
                params: userRole === "student" ? { student_uuid: userId } : {},
            }).then((res) => {
                setPrepItems(res.data || { exams: [], qbank: [] });
            }).catch((err) => {
                if (err.response?.status === 401) { handleLogout(); return; }
                console.log(err);
            });
        }

        async function getStudents() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${window.location.pathname.split('/')[2]}/students`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((res) => {
                const all_students = [];
                for (let i = 0; i < res.data.length; i++) {
                    all_students.push(res.data[i].student);
                    setStudentsStartEndDateMap((prev) => ({
                        ...prev,
                        [res.data[i].student.id]: {
                            start_date: res.data[i].start_date,
                            end_date: res.data[i].end_date,
                        }
                    }));
                }

                setStudents(all_students);
            }).catch((err) => {
                if (err.response?.status === 401)
                    handleLogout();
                else if (err.response?.status !== 404)
                    console.log(err);
            });
        }

        async function getStudentId() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/students/${userId}`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((res) => {
                setStudentId(res.data.id);
            }).catch((err) => {
                if (err.response?.status === 401)
                    handleLogout();
                else if (err.response?.status !== 404)
                    console.log(err);
            });
        }

        async function getClassMaterials() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${window.location.pathname.split('/')[2]}/class-materials`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((res) => {
                setClassMaterials(res.data);
            }).catch((err) => {
                if (err.response?.status === 401)
                    handleLogout();
                else if (err.response?.status !== 404)
                    console.log(err);
            });
        }

        async function getHomeworkTests() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${window.location.pathname.split('/')[2]}/tests`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((res) => {
                // Filter tests that have show_on_homework flag set to true
                const testsForHomework = res.data.filter(test => test.show_on_homework === true);
                setHomeworkTests(testsForHomework);
            }).catch((err) => {
                if (err.response?.status === 401)
                    handleLogout();
                else if (err.response?.status !== 404)
                    console.log(err);
            });
        }

        async function getHomeworkPageOrder() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${window.location.pathname.split('/')[2]}/homework-page/order`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((res) => {
                setHomeworkPageOrder(res.data);
            }).catch((err) => {
                if (err.response?.status === 401)
                    handleLogout();
                else
                    setHomeworkPageOrder([]);
            });
        }

        if (userRole === "student")
            getStudentId();

        getHomework();
        getPrepItems();
        getStudents();
        getClassMaterials();
        getHomeworkTests();
        getHomeworkPageOrder();
        setLoading(false);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch v2 course tests for homework page
    React.useEffect(() => {
        if (!courseData?.id) return;
        v2Api.get(`/courses/${courseData.id}/tests`)
            .then(res => {
                const v2Tests = (res.data.data || []).filter(ct => ct.show_on_homework);
                setV2HomeworkTests(v2Tests);
            })
            .catch(() => {});
    }, [courseData?.id]);

    // Initialize homework type settings from courseData
    React.useEffect(() => {
        if (courseData) {
            setAllowFileUpload(courseData.allow_file_upload_homework !== false);
            setAllowHwQuestions(courseData.allow_hw_questions_homework !== false);
        }
    }, [courseData]);

    // Handler for checkbox changes (just updates local state)
    const handleHomeworkTypeSettingsChange = (setting, value) => {
        if (setting === 'allow_file_upload_homework') {
            setAllowFileUpload(value);
        } else {
            setAllowHwQuestions(value);
        }
        setHasUnsavedSettings(true);
    };

    // Save homework type settings to server
    const saveHomeworkTypeSettings = async () => {
        // Prevent disabling both options
        if (!allowFileUpload && !allowHwQuestions) {
            setErrorAlertMessage("At least one homework type must be enabled!");
            setErrorAlertOpen(true);
            return;
        }

        setSavingSettings(true);
        try {
            await Axios.put(`${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/homework-type-settings`, {
                allow_file_upload_homework: allowFileUpload,
                allow_hw_questions_homework: allowHwQuestions,
            }, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            });
            setSuccessAlertMessage("Homework type settings saved!");
            setSuccessAlertOpen(true);
            setHasUnsavedSettings(false);
        } catch (err) {
            if (err.response?.status === 401) {
                handleLogout();
                return;
            }
            setErrorAlertMessage(err.response?.data?.error || "Failed to save settings");
            setErrorAlertOpen(true);
        } finally {
            setSavingSettings(false);
        }
    };

    // scroll to homeworkIdFromUrl
    React.useEffect(() => {
        const interval = setInterval(() => {
            if (homeworkIdFromUrl !== null && homeworkIdFromUrlRef.current !== null) {
                const element = document.getElementById(homeworkIdFromUrl);
                if (element)
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                clearInterval(interval);
            }

            if (homeworkIdFromUrl === null)
                clearInterval(interval);
        }, 500);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    
    return (
        <Box m="20px" marginX={3} marginY={3} minHeight="90%">
            { loading || courseData === null || lectureModules === null || homeworks === null ? 
                <Loading />
                : <>
                    <Box 
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            px: "25%",
                            ...!matches && {
                                px: "15%",
                            },
                            ...!matches2 && {
                                px: "10%",
                            },
                        }}
                    >
                        <Header 
                            title={
                                courseData.code !== null 
                                ? courseData.code + ". " + courseData.course_title 
                                : courseData.course_title
                            }
                            subtitle="A ONE Institute"
                        />
                        {/* For Admin */}
                        {userRole !== "student" &&
                            <Box
                                minWidth="20rem"
                                width="100%"
                                minHeight="10rem"
                                borderRadius="3px"
                                padding={3}
                                boxShadow="0px 0px 10px 10px rgba(0, 0, 0, 0.10)"
                                backgroundColor={colors.primary[600]}
                                display="flex"
                                flexDirection="column"
                                alignItems="left"
                                gap={3}
                                mb={3}
                            >
                                <Typography variant="h4" color={colors.primary[450]}>
                                    Admin Actions
                                </Typography>
                                {/* Admin-only homework type settings */}
                                {userRole === "admin" && (
                                    <Box
                                        sx={{
                                            p: 2,
                                            backgroundColor: colors.primary[700],
                                            borderRadius: "4px",
                                            border: `1px solid ${colors.primary[500]}`,
                                        }}
                                    >
                                        <Typography variant="h6" mb={1} color={colors.blueAccent[400]}>
                                            Homework Type Settings (Teacher Restrictions)
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" mb={2}>
                                            Control which homework types teachers can create. Admins can always use all options.
                                        </Typography>
                                        <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={allowFileUpload}
                                                        onChange={(e) => handleHomeworkTypeSettingsChange('allow_file_upload_homework', e.target.checked)}
                                                    />
                                                }
                                                label="File Upload (Traditional)"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={allowHwQuestions}
                                                        onChange={(e) => handleHomeworkTypeSettingsChange('allow_hw_questions_homework', e.target.checked)}
                                                    />
                                                }
                                                label="HW Questions (From Textbook)"
                                            />
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={saveHomeworkTypeSettings}
                                                disabled={!hasUnsavedSettings || savingSettings}
                                                sx={{
                                                    ml: 2,
                                                    backgroundColor: colors.blueAccent[600],
                                                    '&:hover': {
                                                        backgroundColor: colors.blueAccent[700],
                                                    },
                                                }}
                                            >
                                                {savingSettings ? 'Saving...' : 'Save'}
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                                <AdminActionAccordion
                                    courseId={courseId}
                                    students={students}
                                    userRole={userRole}
                                    studentsStartEndDateMap={studentsStartEndDateMap}
                                    authorDisabledForAdminActions={authorDisabledForAdminActions}
                                    lectureModules={lectureModules}
                                    setBackdropOpen={setBackdropOpen}
                                    setSuccessAlertOpen={setSuccessAlertOpen}
                                    setErrorAlertOpen={setErrorAlertOpen}
                                    setSuccessAlertMessage={setSuccessAlertMessage}
                                    setErrorAlertMessage={setErrorAlertMessage}
                                    classMaterials={classMaterials}
                                    allowFileUpload={allowFileUpload}
                                    allowHwQuestions={allowHwQuestions}
                                />
                                {userRole === "admin" && ((homeworks?.length || 0) + v2HomeworkTests.length) > 1 &&
                                    <ReorderHomeworkPageModal
                                        courseId={courseId}
                                        homeworks={homeworks}
                                        homeworkTests={v2HomeworkTests}
                                        homeworkPageOrder={homeworkPageOrder}
                                        setSuccessAlertOpen={setSuccessAlertOpen}
                                        setErrorAlertOpen={setErrorAlertOpen}
                                        setSuccessAlertMessage={setSuccessAlertMessage}
                                        setErrorAlertMessage={setErrorAlertMessage}
                                        setBackdropOpen={setBackdropOpen}
                                    />
                                }
                            </Box>
                        }

                        {/* For Students and teachers */}
                        <Box
                            minWidth="20rem"
                            width="100%"
                            minHeight="100%"
                            borderRadius="3px"
                            padding={8}
                            boxShadow="0px 0px 10px 10px rgba(0, 0, 0, 0.10)"
                            backgroundColor={colors.primary[600]}
                            alignItems="center"
                        >
                            {(homeworks !== null && homeworks.length !== 0) || v2HomeworkTests.length > 0 || (prepItems.show_exams_on_homework !== false && prepItems.exams.length > 0) || prepItems.qbank.length > 0 ? (
                                <>
                                    {/* Combine homeworks, v2 tests, and prep items; sort by custom order or due_date DESC */}
                                    {[
                                        ...(homeworks || []).map(hw => ({ ...hw, _type: 'homework' })),
                                        ...v2HomeworkTests.map(ct => ({ ...ct, _type: 'v2_test' })),
                                        // Practice exams appear here only when the program opts in
                                        // (they always show on the Tests page regardless).
                                        ...((prepItems.show_exams_on_homework !== false ? prepItems.exams : []).map(e => ({ ...e, _type: 'ett_exam' }))),
                                        ...prepItems.qbank.map(q => ({ ...q, _type: 'ett_qbank' }))
                                    ]
                                    .sort((a, b) => {
                                        if (homeworkPageOrder && homeworkPageOrder.length > 0) {
                                            const aOrder = homeworkPageOrder.find(
                                                o => o.item_type === a._type && o.item_id === a.id
                                            );
                                            const bOrder = homeworkPageOrder.find(
                                                o => o.item_type === b._type && o.item_id === b.id
                                            );
                                            // Items with custom order come first, sorted by order ASC
                                            if (aOrder && bOrder) return aOrder.order - bOrder.order;
                                            if (aOrder && !bOrder) return -1;
                                            if (!aOrder && bOrder) return 1;
                                        }
                                        // Fallback: sort by due_date DESC
                                        return new Date(b.due_date) - new Date(a.due_date);
                                    })
                                    .map((item) => {
                                        if (item._type === 'homework') {
                                            const homework = item;
                                            let exclude_students = homework.exclude_student ? homework.exclude_student.split('|') : [];
                                            let unexclude_students = homework.unexclude_student ? homework.unexclude_student.split('|') : [];

                                            if (userRole === "student" && studentId !== null
                                                && ((exclude_students.includes(studentId.toString()))
                                                    || (!exclude_students.includes(studentId.toString())
                                                        && !unexclude_students.includes(studentId.toString()))
                                                    )
                                                )
                                                return null;

                                            if (homeworkIdFromUrl !== null && homeworkIdFromUrl === homework.id.toString())
                                                return <div key={`hw-${homework.id}`} id={homework.id} ref={homeworkIdFromUrlRef}>
                                                            <AssignmentBox
                                                                courseId={courseId}
                                                                students={students}
                                                                studentsStartEndDateMap={studentsStartEndDateMap}
                                                                homework={homework}
                                                                classMaterials={classMaterials}
                                                                authorDisabledForAdminActions={authorDisabledForAdminActions}
                                                                setBackdropOpen={setBackdropOpen}
                                                                setSuccessAlertOpen={setSuccessAlertOpen}
                                                                setErrorAlertOpen={setErrorAlertOpen}
                                                                userRole={userRole}
                                                                userId={userId}
                                                                setSuccessAlertMessage={setSuccessAlertMessage}
                                                                setErrorAlertMessage={setErrorAlertMessage}
                                                            />
                                                        </div>

                                            return <AssignmentBox
                                                        key={`hw-${homework.id}`}
                                                        courseId={courseId}
                                                        students={students}
                                                        studentsStartEndDateMap={studentsStartEndDateMap}
                                                        homework={homework}
                                                        classMaterials={classMaterials}
                                                        authorDisabledForAdminActions={authorDisabledForAdminActions}
                                                        setBackdropOpen={setBackdropOpen}
                                                        setSuccessAlertOpen={setSuccessAlertOpen}
                                                        setErrorAlertOpen={setErrorAlertOpen}
                                                        userRole={userRole}
                                                        userId={userId}
                                                        setSuccessAlertMessage={setSuccessAlertMessage}
                                                        setErrorAlertMessage={setErrorAlertMessage}
                                                    />
                                        } else if (item._type === 'v2_test') {
                                            return <TestBoxV2
                                                        key={`v2-test-${item.id}`}
                                                        courseTest={item}
                                                        courseId={courseId}
                                                        students={students}
                                                        studentsStartEndDateMap={studentsStartEndDateMap}
                                                        userRole={userRole}
                                                        userId={userId}
                                                        studentId={studentId}
                                                        setBackdropOpen={setBackdropOpen}
                                                        setSuccessAlertOpen={setSuccessAlertOpen}
                                                        setErrorAlertOpen={setErrorAlertOpen}
                                                        setSuccessAlertMessage={setSuccessAlertMessage}
                                                        setErrorAlertMessage={setErrorAlertMessage}
                                                    />
                                        } else if (item._type === 'ett_exam') {
                                            return <EttExamBox key={`ett-exam-${item.ett_exam_id}`} item={item} />;
                                        } else if (item._type === 'ett_qbank') {
                                            return <EttQbankBox key={`ett-qb-round-${item.round_id}`} item={item} />;
                                        }
                                    })}
                                </>
                            ) : (
                                <Box sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    minWidth: "20rem",
                                    minHeight: "32rem",
                                    gap: "0.4rem",
                                }}>
                                    <InfoOutlinedIcon sx={{ my: "auto" }} />
                                    <Typography variant="h4" color={colors.primary[450]} my="auto">
                                        No homework yet!
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Alert
                        successAlertOpen={successAlertOpen}
                        setSuccessAlertOpen={setSuccessAlertOpen}
                        errorAlertOpen={errorAlertOpen}
                        setErrorAlertOpen={setErrorAlertOpen}
                        successMsg={successAlertMessage}
                        errorMsg={errorAlertMessage}
                        vertical="bottom"
                        horizontal="left"
                    />
                    <Backdrop
                        sx={{ color: colors.primary[400], zIndex: 2147483646 }}
                        open={backdropOpen}
                        onClick={handleBackdropClose}
                    >
                        <CircularProgress color="inherit" />
                    </Backdrop>
                </>
            }
        </Box>
    )
}

export default Homework